<template>
  <div>
    <p class="text-muted">
      Sections are the steps of the client form. Account questions and notices are placed into a
      section by each package; person sections (traders, directors, ...) collect their questions
      automatically. System sections cannot be removed.
    </p>

    <b-row align-h="between" class="mb-1">
      <b-col cols="12" md="3">
        <b-form-input v-model="filter" placeholder="Search..." />
      </b-col>
      <b-col cols="12" md="3" class="d-flex justify-content-end">
        <b-button variant="success" @click="openCreate">
          New section
        </b-button>
      </b-col>
    </b-row>

    <b-table
      striped
      hover
      responsive
      class="builder-table"
      :items="sections"
      :filter="filter"
      :fields="fields"
      :current-page="currentPage"
      :per-page="perPage"
      show-empty
      empty-text="No sections defined."
      @filtered="onFiltered"
    >
      <template #cell(Key)="{ value }">
        <code>{{ value }}</code>
      </template>

      <template #cell(Title)="{ item }">
        <strong>{{ item.Title }}</strong>
        <small v-if="item.Subtitle" class="text-muted d-block">{{ item.Subtitle }}</small>
      </template>

      <template #cell(PersonType)="{ item }">
        <b-badge v-if="item.PersonType" variant="light-primary">
          <feather-icon icon="UsersIcon" size="12" class="mr-25" />{{ item.PersonLabel || item.PersonType }}
        </b-badge>
        <span v-else class="text-muted">—</span>
      </template>

      <template #cell(UsageCount)="{ value }">
        <span v-if="value">Used by {{ plural(value, 'item') }}</span>
        <span v-else class="text-muted">Not used</span>
      </template>

      <template #cell(IsSystem)="{ value }">
        <b-badge v-if="value" variant="light-secondary">System</b-badge>
      </template>

      <template #cell(actions)="{ item }">
        <b-button size="sm" variant="flat-primary" class="btn-icon" title="Edit" @click="openEdit(item)">
          <feather-icon icon="EditIcon" />
        </b-button>
        <b-button
          size="sm"
          variant="flat-danger"
          class="btn-icon"
          title="Delete"
          :disabled="!!item.IsSystem"
          @click="confirmDelete(item)"
        >
          <feather-icon icon="TrashIcon" />
        </b-button>
      </template>
    </b-table>

    <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />

    <b-modal
      v-model="showModal"
      :title="form.id ? 'Edit Section' : 'New Section'"
      ok-title="Save"
      :ok-disabled="saving"
      no-close-on-backdrop
      @ok="handleSave"
    >
      <b-overlay :show="saving">
        <b-form @submit.prevent>
          <b-row v-if="!form.id">
            <b-col cols="12" md="6">
              <b-form-group label="Key">
                <b-form-input v-model="form.key" placeholder="e.g. H or RISK" />
                <small class="text-muted">Optional, generated from the title if empty. Cannot be changed later.</small>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Person type">
                <b-form-select v-model="form.personType" :options="personTypeOptions" />
                <small class="text-muted">A person section lists people (one card each) instead of plain questions.</small>
              </b-form-group>
            </b-col>
          </b-row>
          <b-alert v-else-if="form.isSystem" show variant="secondary" class="p-1">
            System section <code>{{ form.key }}</code>: only the title and subtitle can be changed.
          </b-alert>
          <p v-else class="text-muted small mb-1">
            Key <code>{{ form.key }}</code>
            <span v-if="form.personType"> · person section ({{ form.personType }})</span>
          </p>

          <b-form-group label="Title">
            <b-form-input v-model="form.title" placeholder="e.g. Risk profile" autofocus />
          </b-form-group>
          <b-form-group label="Subtitle">
            <b-form-input v-model="form.subtitle" placeholder="Shown under the title on the client form" />
          </b-form-group>
          <b-form-group v-if="!form.isSystem" label="Sort order">
            <b-form-input v-model.number="form.sortOrder" type="number" min="0" step="1" />
            <small class="text-muted">Sections appear on the form in ascending order.</small>
          </b-form-group>
        </b-form>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow, BCol, BButton, BTable, BPagination, BBadge, BFormInput, BFormGroup, BForm,
  BFormSelect, BModal, BOverlay, BAlert,
} from 'bootstrap-vue'
import store from '@/store'
import { apiError, plural } from './builderHelpers'

function emptyForm() {
  return {
    id: null, key: '', title: '', subtitle: '', sortOrder: 0, personType: null, isSystem: false,
  }
}

export default {
  name: 'SectionsTab',

  components: {
    BRow, BCol, BButton, BTable, BPagination, BBadge, BFormInput, BFormGroup, BForm,
    BFormSelect, BModal, BOverlay, BAlert,
  },

  props: {
    data: { type: Object, required: true },
  },

  data() {
    return {
      filter: '',
      currentPage: 1,
      perPage: 15,
      totalRows: 0,
      showModal: false,
      saving: false,
      form: emptyForm(),
      fields: [
        { key: 'Key', label: 'Key', sortable: true },
        { key: 'Title', label: 'Title', sortable: true },
        { key: 'PersonType', label: 'Person type' },
        { key: 'SortOrder', label: 'Order', sortable: true, class: 'text-center' },
        { key: 'UsageCount', label: 'Usage', sortable: true },
        { key: 'IsSystem', label: '' },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },

  computed: {
    sections() {
      return this.data.sections || []
    },
    personTypeOptions() {
      const types = this.data.personTypes || {}
      return [{ value: null, text: 'None (account section)' }]
        .concat(Object.keys(types).map(key => ({ value: key, text: types[key].label || key })))
    },
  },

  watch: {
    sections: {
      immediate: true,
      handler(list) { this.totalRows = list.length },
    },
  },

  methods: {
    plural,

    onFiltered(items) {
      this.totalRows = items.length
      this.currentPage = 1
    },

    openCreate() {
      const form = emptyForm()
      const max = this.sections.reduce((m, s) => Math.max(m, Number(s.SortOrder) || 0), 0)
      form.sortOrder = max + 10
      this.form = form
      this.showModal = true
    },

    openEdit(section) {
      this.form = {
        id: section.id,
        key: section.Key,
        title: section.Title || '',
        subtitle: section.Subtitle || '',
        sortOrder: Number(section.SortOrder) || 0,
        personType: section.PersonType || null,
        isSystem: !!section.IsSystem,
      }
      this.showModal = true
    },

    handleSave(evt) {
      evt.preventDefault()
      if (!this.form.title.trim()) {
        this.$bvToast.toast('Title is required.', { variant: 'danger', title: 'Validation', solid: true })
        return
      }
      const payload = {
        id: this.form.id || undefined,
        title: this.form.title.trim(),
        subtitle: this.form.subtitle.trim() || null,
        sortOrder: Number(this.form.sortOrder) || 0,
      }
      if (!this.form.id) {
        if (this.form.key.trim()) payload.key = this.form.key.trim()
        if (this.form.personType) payload.personType = this.form.personType
      }
      this.saving = true
      store.dispatch('settings/saveOnboardingFormSection', payload)
        .then(res => {
          this.$bvToast.toast(res.data.message || 'Section saved.', { variant: 'success', title: 'Success', solid: true })
          this.showModal = false
          this.$emit('refresh')
        })
        .catch(err => {
          this.$bvToast.toast(apiError(err), { variant: 'danger', title: 'Error', solid: true })
        })
        .finally(() => { this.saving = false })
    },

    confirmDelete(section) {
      const usage = section.UsageCount
        ? ` It is referenced by ${plural(section.UsageCount, 'package item')}; the server will refuse while it is in use.`
        : ''
      this.$bvModal.msgBoxConfirm(`Delete section "${section.Title}" (${section.Key})?${usage}`, {
        title: 'Delete section',
        okVariant: 'danger',
        okTitle: 'Delete',
        centered: true,
      }).then(ok => {
        if (!ok) return
        store.dispatch('settings/deleteOnboardingFormSection', { id: section.id })
          .then(res => {
            this.$bvToast.toast(res.data.message || 'Section deleted.', { variant: 'success', title: 'Deleted', solid: true })
            this.$emit('refresh')
          })
          .catch(err => {
            this.$bvToast.toast(apiError(err), { variant: 'danger', title: 'Error', solid: true })
          })
      })
    },
  },
}
</script>
